import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AttendancePolicySettingRequest,
  AttendancePolicySettingState,
  initialAttendancePolicySettingState,
} from '../entities/attendance-policy-setting.model';
import { AttendancePolicySettingService } from '../infra/attendance-policy-setting.service';

const OPS = ['load', 'update'] as const;

export const AttendancePolicySettingStore = signalStore(
  { providedIn: 'root' },
  withState<AttendancePolicySettingState>(initialAttendancePolicySettingState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(AttendancePolicySettingService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (policy) => patchState(store, { policy }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('load', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      update: rxMethod<AttendancePolicySettingRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (policy) => patchState(store, { policy }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      blankState: () => {
        patchState(store, initialAttendancePolicySettingState, setPropInit(...OPS));
      },
    }),
  ),
);
